import { useEffect, useMemo, useRef, useState } from "react";
import { rankMenuItems } from "../../../../utils/help/menuSearch/menuSearch";
import { CategoryType } from "../../../../utils/types";
import { BAR_ID } from "../CategoryBarV2/CategoryBarV2";

const SEARCH_RESULTS_CATEGORY_ID = "search-results";

// The bar is sticky, so its rect reports the stuck offset rather than its
// place in the flow, and an offsetTop walk misses any padding on the body.
// Dropping the stickiness for the length of one synchronous read gives the
// real position without either correction.
const flowTop = (element: HTMLElement) => {
  const position = element.style.position;

  element.style.position = "static";

  const top = element.getBoundingClientRect().top + window.scrollY;

  element.style.position = position;

  return top;
};

const SEARCH_TRAVEL_MS = 450;
const KEYBOARD_SETTLE_MS = 1000;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Moves the element by a transform rather than by scrolling. The offset is left
// applied; the caller clears it when it settles the layout underneath.
const slideBy = (element: HTMLElement, distance: number) => {
  element.style.transition = `transform ${SEARCH_TRAVEL_MS}ms ease-in-out`;
  element.style.transform = `translateY(${distance}px)`;
};

const clearSlide = (element: HTMLElement | null) => {
  if (element) {
    element.style.transition = "";
    element.style.transform = "";
  }
};

// Calls back once the on-screen keyboard is gone (at once if it is not up),
// or after a timeout. Returns a cancel.
const whenKeyboardDown = (done: () => void) => {
  const viewport = window.visualViewport;

  if (!viewport || viewport.height >= window.innerHeight - 1) {
    done();

    return () => undefined;
  }

  const controller = new AbortController();
  const finish = () => {
    controller.abort();
    done();
  };
  const timer = setTimeout(finish, KEYBOARD_SETTLE_MS);

  controller.signal.addEventListener("abort", () => clearTimeout(timer));
  viewport.addEventListener(
    "resize",
    () => {
      if (viewport.height >= window.innerHeight - 1) {
        finish();
      }
    },
    { signal: controller.signal }
  );

  return () => controller.abort();
};

/**
 * The single-scroll home's search: the query, its ranked results, and the
 * choreography that opens the field in place and pins it under the header.
 *
 * Attach `rootRef` to the screen root, `searchRegionRef` to the block holding
 * the menu and the results, and `searchInputRef` to the field. While the row is
 * pinned the root carries `data-search-pinned`, which the screen's styles use
 * to set aside everything above the row.
 */
export const useMenuSearch = (
  enabled: boolean,
  menuCategories: CategoryType[] | null
) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchValue), 200);

    return () => clearTimeout(timeout);
  }, [searchValue]);

  const [searchOpen, setSearchOpen] = useState(false);
  const searchRegionRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const cancelUnpinRef = useRef<() => void>();

  // Measures where the row will pin and reserves exactly the space under it,
  // returning the scroll that gets it there. The fill goes straight onto the
  // node rather than through state, so a scroll made in the same tick is not
  // clamped by a height React has not committed yet.
  const alignToPin = () => {
    const bar = document.getElementById(BAR_ID);
    const region = searchRegionRef.current;

    if (!bar || !region) {
      return null;
    }

    const stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
    const barTop = flowTop(bar);
    const regionTop = region.getBoundingClientRect().top + window.scrollY;

    region.style.setProperty(
      "--search-region-top",
      `${regionTop - barTop + stickyTop}px`
    );

    return barTop - stickyTop;
  };

  /*
   * iOS keeps the layout viewport where it was when the keyboard comes up, and
   * a downward scroll after that pans the visible area inside it rather than
   * moving it -- so the fixed header, pinned to the layout viewport, slides off
   * by up to the keyboard's height. A scroll made in the same tap as the focus
   * still lands after the keyboard, so opening never scrolls the page down.
   * Instead the page is carried to the pin by a transform, and once there
   * everything above the row is set aside, which leaves the pin at or above
   * the current scroll. Where the row still sits lower than that, the root is
   * lifted by the remainder. Scrolling up is safe, and is how the page is
   * moved when the row is already stuck.
   */
  const pinRow = () => {
    const root = rootRef.current;

    if (!root) {
      return;
    }

    root.dataset.searchPinned = "";
    root.style.marginBlockStart = "";

    const target = alignToPin();

    if (target === null) {
      return;
    }

    const shortfall = target - window.scrollY;

    if (shortfall > 0) {
      root.style.marginBlockStart = `${-shortfall}px`;
    } else {
      window.scrollTo({ top: target });
    }
  };

  // Brings back what pinRow set aside, scrolling by the height it returns so
  // the row does not move. Run with the keyboard gone, as it scrolls down.
  const unpinRow = () => {
    const root = rootRef.current;
    const bar = document.getElementById(BAR_ID);

    if (!root || !bar || root.dataset.searchPinned === undefined) {
      return;
    }

    const before = flowTop(bar);

    delete root.dataset.searchPinned;
    root.style.marginBlockStart = "";
    window.scrollTo({ top: window.scrollY + flowTop(bar) - before });
  };

  const openSearch = () => {
    cancelUnpinRef.current?.();

    const root = rootRef.current;
    const target = alignToPin();

    // Already stuck, the row is where it pins and nothing needs to travel: the
    // menu fades out under it in place, and the settle tidies the scroll.
    if (root && target !== null && target > window.scrollY) {
      if (prefersReducedMotion()) {
        pinRow();
      } else {
        slideBy(root, window.scrollY - target);
      }
    }

    setSearchOpen(true);
  };

  const closeSearch = () => {
    (document.activeElement as HTMLElement | null)?.blur();
    clearSlide(rootRef.current);
    cancelUnpinRef.current = whenKeyboardDown(unpinRow);
    setSearchValue("");
    setSearchOpen(false);
  };

  useEffect(() => () => cancelUnpinRef.current?.(), []);

  // Once the slide has landed, swap the transform for the pinned layout, and
  // hand focus from the stand-in the tap focused to the real field, which now
  // sits under the header where the keyboard cannot cover it.
  useEffect(() => {
    if (!enabled || !searchOpen) {
      return;
    }

    const settle = setTimeout(() => {
      clearSlide(rootRef.current);
      pinRow();

      const input = searchInputRef.current;

      input?.focus({ preventScroll: true });
      input?.setSelectionRange(input.value.length, input.value.length);
    }, SEARCH_TRAVEL_MS);

    return () => clearTimeout(settle);
  }, [enabled, searchOpen]);

  useEffect(() => {
    if (!enabled || !searchOpen || searchValue) {
      return;
    }

    const collapseOnOutsideTap = (event: MouseEvent | TouchEvent) => {
      if (!document.getElementById(BAR_ID)?.contains(event.target as Node)) {
        closeSearch();
      }
    };

    document.addEventListener("mousedown", collapseOnOutsideTap);
    document.addEventListener("touchstart", collapseOnOutsideTap);

    return () => {
      document.removeEventListener("mousedown", collapseOnOutsideTap);
      document.removeEventListener("touchstart", collapseOnOutsideTap);
    };
  }, [enabled, searchOpen, searchValue]);

  const searchResults = useMemo((): CategoryType[] => {
    if (!debouncedSearch || !menuCategories?.length) {
      return [];
    }

    const ranked = rankMenuItems(menuCategories, debouncedSearch);

    return ranked.length
      ? [
          {
            ...menuCategories[0],
            id: SEARCH_RESULTS_CATEGORY_ID,
            itemCategories: ranked.map((result) => result.itemCategory),
          },
        ]
      : [];
  }, [menuCategories, debouncedSearch]);

  return {
    searchValue,
    setSearchValue,
    debouncedSearch,
    searchResults,
    searchOpen,
    openSearch,
    closeSearch,
    rootRef,
    searchRegionRef,
    searchInputRef,
  };
};
